import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard,
  Users,
  Mail,
  FileText,
  Megaphone,
  Newspaper,
  Radio,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  ExternalLink,
  ShieldCheck,
  ChevronDown,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/registrations", label: "Registrations", icon: Users, testid: "nav-registrations" },
  { to: "/admin/broadcasts", label: "Broadcasts", icon: Megaphone, testid: "nav-broadcasts" },
  { to: "/admin/news", label: "News", icon: Newspaper, testid: "nav-news" },
  { to: "/admin/live", label: "Live Broadcast", icon: Radio, testid: "nav-live" },
  { to: "/admin/email-templates", label: "Email Templates", icon: FileText, testid: "nav-email-templates" },
  { to: "/admin/email-logs", label: "Email Logs", icon: Mail, testid: "nav-email-logs" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f0f0f1] flex flex-col" data-testid="admin-layout">
      {/* Top Admin Bar (WordPress-style) */}
      <header className="bg-[#1d2327] text-white h-12 flex items-center justify-between px-4 sticky top-0 z-40 shadow">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="lg:hidden p-1.5 hover:bg-white/10 rounded"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-gradient-to-br from-[#FF9933] to-[#E67300] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <span className="font-semibold text-sm hidden sm:inline">Kumbh 2028 Admin</span>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex items-center gap-1 text-xs text-white/70 hover:text-white px-2 py-1 hover:bg-white/10 rounded transition-colors"
            data-testid="visit-site-link"
          >
            <ExternalLink className="w-3 h-3" />
            Visit Site
          </a>
        </div>

        <div className="relative">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-2 text-sm hover:bg-white/10 px-2 py-1 rounded transition-colors"
            data-testid="user-menu-toggle"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF9933] to-[#CC6600] flex items-center justify-center text-xs font-bold">
              {(user?.name || user?.email || "A").charAt(0).toUpperCase()}
            </div>
            <span className="hidden sm:inline">{user?.name || "Admin"}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setUserMenuOpen(false)}
              />
              <div
                className="absolute right-0 mt-1 w-56 bg-white text-gray-800 rounded-md shadow-lg border border-gray-200 z-20 overflow-hidden"
                data-testid="user-menu-dropdown"
              >
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold">{user?.name || "Administrator"}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-red-50 hover:text-red-600 transition-colors text-left"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-12 left-0 h-[calc(100vh-3rem)] w-60 bg-[#1d2327] text-white flex-shrink-0 z-30 transform transition-transform lg:transform-none ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
          data-testid="admin-sidebar"
        >
          <nav className="py-4 flex flex-col h-full">
            <ul className="space-y-0.5 px-2 flex-1">
              {NAV.map(({ to, label, icon: Icon, end, testid }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                        isActive
                          ? "bg-[#FF9933] text-white font-medium shadow-sm"
                          : "text-white/70 hover:bg-white/10 hover:text-white"
                      }`
                    }
                    data-testid={testid}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="px-4 py-3 border-t border-white/10 text-xs text-white/40">
              <p>Ujjain Maha Kumbh 2028</p>
              <p className="mt-0.5">Community Admin Panel</p>
            </div>
          </nav>
        </aside>

        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-20"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-x-auto text-gray-900">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
